const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v) => (typeof v === "number" ? `${v}%` : "N/A");

const MapDistrictDetailCard = ({ title, scoreLabel, score, badge, rows = [], emptyMessage }) => (
  <article className="ed-map-detail-card">
    <div className="ed-tip-top">
      <b>{title}</b>
      {badge ? <span className={`ed-map-badge ${badge.className || ""}`}>{badge.label}</span> : null}
    </div>
    {scoreLabel ? (
      <div className="ed-tip-score">
        {scoreLabel}
        <strong>{score ?? "N/A"}</strong>
      </div>
    ) : null}
    {rows.length ? (
      <div className="ed-tip-grid">
        {rows.map((row) => (
          <div className="ed-tip-row" key={row.label}>
            <span>{row.label}</span>
            <b>{row.value}</b>
          </div>
        ))}
      </div>
    ) : (
      <p className="ed-map-detail-empty">{emptyMessage || "No data available for this district."}</p>
    )}
  </article>
);

export const getStatusBadge = (value) => {
  if (value == null) return { label: "No Data", className: "gray" };
  if (value >= 70) return { label: "Good", className: "" };
  if (value >= 50) return { label: "Average", className: "gray" };
  return { label: "Low", className: "red" };
};

export const buildDistrictOverviewCard = (districtName, district, metricMeta, selectedMetric) => {
  if (!district?.has_data) return null;
  const metricValue =
    selectedMetric === "shi"
      ? district.metrics?.shi
      : district.metrics?.[selectedMetric] ?? district.metrics?.shi;
  const score =
    selectedMetric === "shi" && typeof metricValue === "number"
      ? `${metricValue}%`
      : typeof metricValue === "number"
        ? `${metricValue}%`
        : metricValue ?? "N/A";

  return {
    title: `${districtName} District`,
    scoreLabel: metricMeta.legend,
    score,
    badge: getStatusBadge(metricValue),
    rows: [
      { label: "Schools", value: fmt(district.total_schools) },
      { label: "Students", value: fmt(district.total_students) },
      { label: "Aadhaar", value: pct(district.metrics?.aadhaar_coverage) },
      { label: "APAAR", value: pct(district.metrics?.apaar_coverage) },
    ],
  };
};

export const buildPgiCard = (district) => {
  if (!district?.has_data) return null;
  const m = district.metrics || {};
  const identity =
    typeof m.aadhaar_coverage === "number" && typeof m.apaar_coverage === "number"
      ? Math.round(((m.aadhaar_coverage + m.apaar_coverage) / 2) * 10) / 10
      : m.aadhaar_coverage ?? m.apaar_coverage ?? null;

  const parts = [
    { v: m.shi, w: 0.35 },
    { v: m.infrastructure_index, w: 0.25 },
    { v: m.completion_rate, w: 0.2 },
    { v: identity, w: 0.2 },
  ].filter((p) => typeof p.v === "number");

  const score =
    parts.length > 0
      ? Math.round((parts.reduce((s, p) => s + p.v * p.w, 0) / parts.reduce((s, p) => s + p.w, 0)) * 10) / 10
      : null;

  return {
    title: "PGI",
    scoreLabel: "Performance Grading Index",
    score: score != null ? `${score}%` : "N/A",
    badge: getStatusBadge(score),
    rows: [
      { label: "Learning Outcomes", value: pct(m.shi) },
      { label: "Infrastructure", value: pct(m.infrastructure_index) },
      { label: "Access & Equity", value: pct(m.completion_rate) },
      { label: "Identity Compliance", value: pct(identity) },
    ],
  };
};

export const buildSqaafCard = (districtName, sqaafOverview) => {
  const isPune = String(districtName || "").trim().toLowerCase() === "pune";
  if (!isPune || !sqaafOverview) {
    return {
      title: "SQAAF",
      scoreLabel: "School Quality Assessment",
      score: "N/A",
      badge: { label: "No Data", className: "gray" },
      rows: [],
      emptyMessage: "SQAAF assessment data is available for Pune district.",
    };
  }

  const score = sqaafOverview.district_mean;
  const strongPlus = sqaafOverview.strong_plus_exemplary_pct;

  return {
    title: "SQAAF",
    scoreLabel: "District SQAAF Score",
    score: typeof score === "number" ? `${score}%` : "N/A",
    badge: getStatusBadge(score),
    rows: [
      { label: "Schools Assessed", value: fmt(sqaafOverview.n_schools) },
      { label: "Strong + Exemplary", value: pct(strongPlus) },
      { label: "Completion Rate", value: pct(sqaafOverview.completion_rate) },
      { label: "Below 70% Score", value: pct(sqaafOverview.schools_below_70_pct) },
    ],
  };
};

export const buildPatCard = (district) => {
  if (!district?.has_data) return null;
  const m = district.metrics || {};
  const parts = [
    { v: m.completion_rate, w: 0.4 },
    { v: m.aadhaar_coverage, w: 0.3 },
    { v: m.apaar_coverage, w: 0.3 },
  ].filter((p) => typeof p.v === "number");

  const score =
    parts.length > 0
      ? Math.round((parts.reduce((s, p) => s + p.v * p.w, 0) / parts.reduce((s, p) => s + p.w, 0)) * 10) / 10
      : null;

  return {
    title: "PAT",
    scoreLabel: "Periodic Assessment Readiness",
    score: score != null ? `${score}%` : "N/A",
    badge: getStatusBadge(score),
    rows: [
      { label: "Data Completion", value: pct(m.completion_rate) },
      { label: "Aadhaar Coverage", value: pct(m.aadhaar_coverage) },
      { label: "APAAR Coverage", value: pct(m.apaar_coverage) },
      { label: "CTET Qualified", value: pct(m.ctet_qualified_pct) },
    ],
  };
};

export default MapDistrictDetailCard;
